import { useEffect, useRef, useState } from 'react';
import type { RoleRequest, Stewardship } from '@accounts/domain';
import type { Moment } from '@beyond-js/ui/react';
import { useRead } from '@accounts/ui';

/** An organization's open role requests as its members region shows them */
export interface Requests {
	/** Undefined until they are first read, and for a member who does not decide on them */
	list?: RoleRequest[];
	/** Their read did not answer: said in their place, over what was kept */
	unavailable: boolean;
	/** When the requests kept were read, while a later read does not answer; null when not known */
	updated?: Moment | null;
}

/**
 * The open role requests of an organization, read on their own for its owners and administrators, who
 * decide on them (ARR-11). Their read is one of the page's: when Beyond Accounts does not answer it, it
 * joins the page's one notice (D163) and the members region says so where the requests would be, never
 * as no requests. A member who does not decide on them reads nothing.
 *
 * @param steward Whether the person decides on the requests
 * @param renewed Changes when the page's session is renewed, which reads them again
 */
export function useRequests(stewardship: Stewardship, organization: string, steward: boolean, renewed: unknown) {
	const [list, setList] = useState<RoleRequest[]>();
	// An answer for an organization no longer in view is not shown
	const latest = useRef(organization);
	latest.current = organization;
	const { unread, at, run } = useRead(async () => {
		const found = steward ? await stewardship.requests(organization) : undefined;
		if (latest.current === organization) setList(found);
	});

	useEffect(() => {
		setList(undefined);
		void run();
	}, [run, organization, steward, renewed]);

	const outage = Boolean(unread?.unreachable);
	const requests: Requests = { list, unavailable: outage, updated: outage && list ? (at ?? null) : undefined };
	return { requests, unread, run };
}
